import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, C, errorMessage } from "../lib/api";
import { AssetImage } from "../components/AssetImage";
import { ForgotPasswordDialog } from "../components/ForgotPasswordDialog";

export default function Landing() {
  const { login, register, guest } = useAuth();
  const [adult, setAdult] = useState(false);
  const hasGuest = !!localStorage.getItem("pfp_guest_token");
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [signupsOpen, setSignupsOpen] = useState(true); // invite-only rooms show Sign in only
  const [form, setForm] = useState({ email: "", password: "", password2: "", display_name: "", dob: "", referral_code: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [promo, setPromo] = useState(null); // {running, chips, ends_at}
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => {
    api.get(C.ApiRoutes.AUTH_CONFIG).then((r) => { setSignupsOpen(r.data.signups_open); if (!r.data.signups_open) setMode("login"); }).catch(() => {});
    api.get("/api/promo").then((r) => setPromo(r.data)).catch(() => {});
  }, []);
  const promoDays = promo?.running ? Math.max(1, Math.ceil((new Date(promo.ends_at) - Date.now()) / 86400000)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        if (form.password !== form.password2) { setErr("The two passwords don't match"); setBusy(false); return; }
        const data = await register(form.email, form.password, form.display_name, form.dob, form.referral_code.trim() || undefined);
        if (data?.welcome) sessionStorage.setItem("pfp_welcome", JSON.stringify(data.welcome));
      }
      nav("/lobby");
    } catch (ex) {
      if (ex?.response?.data?.detail === "REGION_BLOCKED") return nav("/blocked");
      setErr(errorMessage(ex));
    } finally { setBusy(false); }
  };

  const playAsGuest = async () => {
    setErr(""); setBusy(true);
    try { await guest(adult); nav("/lobby"); } catch (ex) {
      if (ex?.response?.data?.detail === "REGION_BLOCKED") return nav("/blocked");
      setErr(errorMessage(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative noise">
      <section className="flex-1 px-8 py-6 lg:px-14 lg:py-8 flex flex-col items-center justify-center text-center min-h-screen gap-5">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-[1.05] rise">Every Shuffle <span className="text-amber-400">VERIFIABLE.</span></h1>
        <div className="flex-1 relative flex items-center justify-center w-full min-h-0 rise overflow-hidden" style={{ animationDelay: "80ms", maxHeight: "calc(100vh - 19.5rem)" }} data-testid="landing-hero">
          <AssetImage id="bg_landing" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-50" style={{ filter: "blur(22px) saturate(0.9)", transform: "scale(1.15)", maskImage: "radial-gradient(ellipse 55% 60% at 50% 50%, #000 35%, transparent 100%)", WebkitMaskImage: "radial-gradient(ellipse 55% 60% at 50% 50%, #000 35%, transparent 100%)" }} />
          <AssetImage id="bg_landing" className="relative w-auto max-w-full h-full object-contain" style={{ maskImage: "linear-gradient(to right, transparent, #000 10%, #000 90%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, #000 10%, #000 90%, transparent)" }} />
        </div>
        <div className="rise max-w-3xl" style={{ animationDelay: "160ms" }}>
          <p className="text-base md:text-lg text-slate-300">The deck is committed to before a card is dealt — and you can verify it yourself after every hand.</p>
          <div className="mt-3 font-display text-sm md:text-base tracking-[0.25em] uppercase text-amber-400" data-testid="landing-tagline">Real action — not forced</div>
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4" /> Australian First · Entertainment Only · Chips are NON Redeemable</div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="text-slate-600">18+</span>
            <Link to="/terms" className="hover:text-amber-300 underline underline-offset-4" data-testid="landing-terms-link">Terms &amp; Conditions</Link>
            <span className="text-slate-700">·</span>
            <Link to="/privacy" className="hover:text-amber-300 underline underline-offset-4" data-testid="landing-privacy-link">Privacy Policy</Link>
            <span className="text-slate-700">·</span>
            <Link to="/contact" className="hover:text-amber-300 underline underline-offset-4" data-testid="landing-contact-link">Contact us</Link>
          </div>
          <div className="text-[11px] text-slate-500" data-testid="landing-copyright">© {new Date().getFullYear()} Players Room. All rights reserved.</div>
        </div>
      </section>
      <section className="lg:w-[400px] flex items-center justify-center p-8 bg-slate-900/40">
        <form onSubmit={submit} data-testid="auth-form" className="glass rounded-3xl p-8 w-full flex flex-col gap-4 rise" style={{ animationDelay: "120ms" }}>
          {signupsOpen ? (
            <div className="flex gap-1 bg-black/30 rounded-full p-1">
              {["login", "register"].map((m) => (
                <button type="button" key={m} data-testid={`auth-mode-${m}`} onClick={() => { setMode(m); setErr(""); }}
                  className={`flex-1 rounded-full py-2 text-sm capitalize ${mode === m ? "bg-amber-500 text-slate-900" : "text-slate-300 hover:text-white"}`}>{m === "login" ? "Sign in" : "Create account"}</button>
              ))}
            </div>
          ) : (
            <div data-testid="invite-only-notice" className="rounded-2xl border border-amber-400/30 bg-amber-400/5 px-4 py-3 text-center">
              <div className="font-display text-sm tracking-[0.2em] text-amber-300">INVITE ONLY</div>
              <p className="text-xs text-slate-400 mt-1">Sign in with the details the host sent you.</p>
            </div>
          )}
          {mode === "register" && promo?.running && (
            <div data-testid="promo-banner" className="rounded-2xl border border-amber-400/50 bg-amber-400/10 px-4 py-3 text-center">
              <div className="font-display text-sm tracking-[0.15em] text-amber-200">DOUBLE CHIPS FOR NEW PLAYERS</div>
              <p className="text-xs text-slate-300 mt-1">$10,000 to start plus a ${promo.chips.toLocaleString()} bonus — {promoDays} day{promoDays === 1 ? "" : "s"} left.</p>
            </div>
          )}
          {mode === "register" ? (<>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Name
              <input data-testid="auth-display-name" required minLength={2} maxLength={24} value={form.display_name} onChange={set("display_name")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Date of birth <span className="text-slate-500">· 18+ only</span>
              <input data-testid="auth-dob" type="date" required max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={set("dob")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Email
              <input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Password
              <input data-testid="auth-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Confirm password
              <input data-testid="auth-password2" type="password" required minLength={8} value={form.password2} onChange={set("password2")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400 mt-3">Referral code <span className="text-slate-500">(optional)</span>
              <input data-testid="auth-referral-code" maxLength={12} placeholder="PR000000" value={form.referral_code} onChange={set("referral_code")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base font-mono tracking-widest text-slate-100 focus:border-amber-400 outline-none uppercase" />
            </label>
          </>) : (<>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Email
              <input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Password
              <input data-testid="auth-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
          </>)}
          {err && <div data-testid="auth-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
          {mode === "login" && (
            <button type="button" data-testid="auth-forgot" onClick={() => setForgot(true)} className="text-xs text-slate-400 hover:text-amber-300 underline underline-offset-4 self-center">
              Forgot your sign-in details?
            </button>
          )}
          <button data-testid="auth-submit" disabled={busy} className="rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift">{busy ? "…" : mode === "login" ? "PLAY NOW" : `Claim $${((promo?.running ? promo.chips : 0) + 10000).toLocaleString()} chips`}</button>
          {signupsOpen && (
            <div className="flex flex-col gap-2 pt-3 border-t border-white/10" data-testid="guest-block">
              {!hasGuest && (
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer self-center">
                  <input data-testid="guest-adult-check" type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} className="accent-amber-500 w-4 h-4" />
                  I am 18 or over
                </label>
              )}
              <button type="button" data-testid="guest-play-button" disabled={busy || (!hasGuest && !adult)} onClick={playAsGuest}
                className="rounded-full py-2.5 font-display text-xs tracking-wider border border-amber-400/50 text-amber-200 hover:bg-amber-500/10 disabled:opacity-40 btn-lift">
                {hasGuest ? "CONTINUE AS GUEST" : "PLAY AS GUEST"}
              </button>
              <p className="text-[11px] text-slate-500 text-center">No sign-up needed. Join any time for 50,000 chips — and keep every chip you've won.</p>
            </div>
          )}
        </form>
      </section>
      {forgot && <ForgotPasswordDialog onClose={() => setForgot(false)} />}
    </div>
  );
}
